import { Directive, Input, HostBinding } from '@angular/core';

/**
 * Applies sticky positioning through CSS custom properties.
 *
 * Inputs map to CSS variables consumed by styles:
 * - `stickyTop?: number | string` → `--bazis-sticky-top` (px if number)
 * - `stickyBottom?: number | string` → `--bazis-sticky-bottom`
 * - `stickyStart?: number | string` → `--bazis-sticky-start`
 * - `stickyEnd?: number | string` → `--bazis-sticky-end`
 * - `stickyZIndex?: number` → `--bazis-sticky-z`
 *
 * Example:
 * ```html
 * <header sticky [stickyTop]="0" [stickyZIndex]="1000">...</header>
 * ```
 */
@Directive({
    selector: '[sticky]',
    standalone: true,
})
export class StickyDirective {
    /**
     * Distance from the top edge where the element should stick.
     */
    @Input() stickyTop: number | string;

    /**
     * Distance from the bottom edge where the element should stick.
     */
    @Input() stickyBottom: number | string;

    /**
     * Distance from the inline start (LTR: left, RTL: right).
     */
    @Input() stickyStart: number | string;

    /**
     * Distance from the inline end (LTR: right, RTL: left).
     */
    @Input() stickyEnd: number | string;

    /**
     * Z-index layer to use while sticky.
     */
    @Input() stickyZIndex: number;

    /**
     * Enables sticky base class.
     */
    @HostBinding('class.bh-sticky') sticky: boolean = true;

    /**
     * CSS variable `--bazis-sticky-top` derived from `stickyTop`.
     */
    @HostBinding('style.--bazis-sticky-top') get topPosition() {
        if (this.stickyTop !== undefined) return `${this.stickyTop}px`;
    }

    /**
     * CSS variable `--bazis-sticky-bottom` derived from `stickyBottom`.
     */
    @HostBinding('style.--bazis-sticky-bottom') get bottomPosition() {
        if (this.stickyBottom !== undefined) return `${this.stickyBottom}px`;
    }

    /**
     * CSS variable `--bazis-sticky-start` derived from `stickyStart`.
     */
    @HostBinding('style.--bazis-sticky-start') get startPosition() {
        if (this.stickyStart !== undefined) return `${this.stickyStart}px`;
    }

    /**
     * CSS variable `--bazis-sticky-end` derived from `stickyEnd`.
     */
    @HostBinding('style.--bazis-sticky-end') get endPosition() {
        if (this.stickyEnd !== undefined) return `${this.stickyEnd}px`;
    }

    /**
     * CSS variable `--bazis-sticky-z` derived from `stickyZIndex`.
     */
    @HostBinding('style.--bazis-sticky-z') get zIndexLayer() {
        if (this.stickyZIndex !== undefined) return this.stickyZIndex;
    }

    constructor() {}
}
